// Браузерная сторона push: поддержка, подписка этого устройства, подпись устройства.
// Web Push на iPhone и iPad работает только у приложения, добавленного на экран «Домой»
// (iOS 16.4+): в обычной вкладке Safari PushManager недоступен — показываем подсказку.

export type PushSupport = 'supported' | 'ios-needs-install' | 'unsupported'

export type BrowserEnv = {
  userAgent: string
  maxTouchPoints: number
  standalone: boolean
  hasServiceWorker: boolean
  hasPushManager: boolean
  hasNotification: boolean
}

const DEVICE_ID_KEY = 'push:subscription-id'

export function isIos(userAgent: string, maxTouchPoints: number): boolean {
  if (/iPhone|iPad|iPod/.test(userAgent)) return true
  // iPadOS выдаёт себя за Mac, но у него есть сенсорный экран.
  return /Macintosh/.test(userAgent) && maxTouchPoints > 1
}

export function detectSupport(env: BrowserEnv): PushSupport {
  const ios = isIos(env.userAgent, env.maxTouchPoints)
  if (ios && !env.standalone) return 'ios-needs-install'
  if (!env.hasServiceWorker || !env.hasPushManager || !env.hasNotification) return 'unsupported'
  return 'supported'
}

export function currentEnv(): BrowserEnv {
  const nav = navigator as Navigator & { standalone?: boolean }
  const standalone =
    nav.standalone === true || (typeof window.matchMedia === 'function' && window.matchMedia('(display-mode: standalone)').matches)
  return {
    userAgent: nav.userAgent,
    maxTouchPoints: nav.maxTouchPoints ?? 0,
    standalone,
    hasServiceWorker: 'serviceWorker' in nav,
    hasPushManager: typeof window !== 'undefined' && 'PushManager' in window,
    hasNotification: typeof window !== 'undefined' && 'Notification' in window,
  }
}

/** «iPhone · Safari», «Android · Chrome», «Windows · Firefox» — подпись в списке устройств. */
export function deviceLabel(userAgent: string, maxTouchPoints = 0): string {
  let system = 'Устройство'
  if (/iPhone/.test(userAgent)) system = 'iPhone'
  else if (/iPad/.test(userAgent) || (/Macintosh/.test(userAgent) && maxTouchPoints > 1)) system = 'iPad'
  else if (/Android/.test(userAgent)) system = 'Android'
  else if (/Windows/.test(userAgent)) system = 'Windows'
  else if (/Macintosh|Mac OS X/.test(userAgent)) system = 'Mac'
  else if (/Linux/.test(userAgent)) system = 'Linux'

  let browser = 'браузер'
  if (/Edg\//.test(userAgent)) browser = 'Edge'
  else if (/YaBrowser\//.test(userAgent)) browser = 'Яндекс Браузер'
  else if (/OPR\//.test(userAgent)) browser = 'Opera'
  else if (/Firefox\/|FxiOS\//.test(userAgent)) browser = 'Firefox'
  else if (/Chrome\/|CriOS\//.test(userAgent)) browser = 'Chrome'
  else if (/Safari\//.test(userAgent)) browser = 'Safari'
  return `${system} · ${browser}`
}

/** Публичный ключ VAPID (base64url) → байты для applicationServerKey. */
export function urlBase64ToUint8Array(base64: string): Uint8Array<ArrayBuffer> {
  const padded = (base64 + '='.repeat((4 - (base64.length % 4)) % 4)).replace(/-/g, '+').replace(/_/g, '/')
  const raw = atob(padded)
  const out = new Uint8Array(new ArrayBuffer(raw.length))
  for (let i = 0; i < raw.length; i += 1) out[i] = raw.charCodeAt(i)
  return out
}

async function registration(): Promise<ServiceWorkerRegistration> {
  const timeout = new Promise<never>((_, reject) =>
    setTimeout(() => reject(new Error('Служба уведомлений не запущена — обнови страницу')), 10_000),
  )
  return Promise.race([navigator.serviceWorker.ready, timeout])
}

export async function currentSubscription(): Promise<PushSubscription | null> {
  if (!('serviceWorker' in navigator)) return null
  const reg = await navigator.serviceWorker.getRegistration()
  return (await reg?.pushManager.getSubscription()) ?? null
}

export type DeviceSubscription = { endpoint: string; keys: { p256dh: string; auth: string } }

/** Подписать это устройство (разрешение уже дано). */
export async function subscribeDevice(publicKey: string): Promise<DeviceSubscription> {
  const reg = await registration()
  const existing = await reg.pushManager.getSubscription()
  const subscription =
    existing ??
    (await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlBase64ToUint8Array(publicKey) }))
  const json = subscription.toJSON()
  const p256dh = json.keys?.p256dh
  const auth = json.keys?.auth
  if (!json.endpoint || !p256dh || !auth) throw new Error('Браузер не выдал ключи подписки')
  return { endpoint: json.endpoint, keys: { p256dh, auth } }
}

/** Отписать это устройство в браузере. Возвращает endpoint, чтобы удалить его и на сервере. */
export async function unsubscribeDevice(): Promise<string | null> {
  const subscription = await currentSubscription()
  if (!subscription) return null
  const endpoint = subscription.endpoint
  await subscription.unsubscribe()
  return endpoint
}

export function rememberDeviceId(id: string | null): void {
  try {
    if (id) localStorage.setItem(DEVICE_ID_KEY, id)
    else localStorage.removeItem(DEVICE_ID_KEY)
  } catch {
    // приватный режим — просто не отмечаем «это устройство»
  }
}

export function rememberedDeviceId(): string | null {
  try {
    return localStorage.getItem(DEVICE_ID_KEY)
  } catch {
    return null
  }
}

/** «08:00:00» → «08:00» для <input type="time">. */
export function shortTime(value: string | null | undefined): string {
  return value ? value.slice(0, 5) : ''
}
